import { ChangeDetectionStrategy, Component, computed, input, ViewEncapsulation } from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxControlContext, HxControlVariant } from '../../base/types';

export type BadgeVariant = Extract<HxControlVariant, 'fill' | 'soft' | 'outline'>;
export type BadgeContext = HxControlContext;
export type BadgeType = 'dot' | 'numeric' | 'text';
export type BadgeShape = 'rounded' | 'square';

/**
 * Badge component for displaying status indicators and notification counters.
 *
 * @example
 *   ```html
 *   <!-- Numeric badge with notification count -->
 *   <hx-badge label="5" context="positive"></hx-badge>
 *
 *   <!-- Dot badge for status indicator -->
 *   <hx-badge type="dot" context="positive" ariaLabel="Online"></hx-badge>
 *
 *   <!-- Outline variant for subtle emphasis -->
 *   <hx-badge label="NEW" variant="outline" context="info"></hx-badge>
 *
 *   <!-- Square badge for compact status chips -->
 *   <hx-badge label="STRONG" type="text" shape="square"></hx-badge>
 *
 *   <!-- Status badge with leading status dot -->
 *   <hx-badge label="Completed" type="text" variant="soft" context="positive" showStatusDot></hx-badge>
 *   ```;
 *
 * @see {@link https://ui.moondesignsystem.com/?path=/docs/indicators-status-badge--docs Moon Design System Badge}
 */
@Component({
  selector: 'hx-badge',
  standalone: true,
  templateUrl: './badge.html',
  styleUrls: ['./badge.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': 'badgeClasses()',
    '[attr.aria-label]': 'ariaLabel()',
    '[attr.role]': '"status"',
    '[style.background-color]': 'customBackgroundColor()',
    '[style.color]': 'customTextColor()',
    '[style.box-shadow]': 'customBoxShadow()',
  },
})
export class HxBadge extends HxBaseControl {
  /**
   * Visual presentation mode.
   *
   * - `fill`: High contrast solid background (default)
   * - `soft`: Reduced contrast lighter background
   * - `outline`: Transparent background with border
   *
   * @default 'fill'
   */
  readonly variant = input<BadgeVariant>('fill');

  /**
   * Semantic meaning through color.
   *
   * - `brand`: Primary brand identity (default)
   * - `neutral`: No semantic meaning (gray)
   * - `positive`: Success, confirmation (green)
   * - `negative`: Errors, destructive actions (red)
   * - `caution`: Warnings, needs attention (yellow/amber)
   * - `info`: Informational, helpful (blue/teal)
   *
   * @default 'brand'
   */
  readonly context = input<BadgeContext>('brand');

  /**
   * Content display mode.
   *
   * - `dot`: Empty status indicator (8x8px circle, ignores label)
   * - `numeric`: Text/number display (16px minimum width, auto-expands)
   * - `text`: Text display with increased padding (for longer text labels)
   *
   * @default 'dot'
   */
  readonly type = input<BadgeType>('dot');

  /**
   * Whether to render a leading status dot before the badge label.
   *
   * Useful for status chips like Completed, Cancelled, or In Progress.
   *
   * @default false
   */
  readonly showStatusDot = input<boolean>(false);

  /**
   * Corner style for the badge.
   *
   * - `rounded`: Fully rounded pill shape (default)
   * - `square`: Reduced corner radius for compact status chips
   *
   * @default 'rounded'
   */
  readonly shape = input<BadgeShape>('rounded');

  /**
   * Optional custom color for dot badges.
   *
   * When provided on a dot badge, the component uses this color for the dot fill and removes the default token stroke
   * so the badge can match chart legend swatches or other custom palette uses.
   *
   * @default undefined
   */
  readonly color = input<string | undefined>(undefined);

  /**
   * Optional custom background color for the badge.
   *
   * Use this when you want a custom pill fill while keeping the badge's semantic text color.
   *
   * @default undefined
   */
  readonly backgroundColor = input<string | undefined>(undefined);

  /**
   * Optional custom text color for the badge.
   *
   * Use this when a status chip needs an exact text color that differs from the semantic context color.
   *
   * @default undefined
   */
  readonly textColor = input<string | undefined>(undefined);

  /**
   * CSS class object for host element binding.
   *
   * Generates BEM classes based on variant and context:
   *
   * - Base class: `hx-badge`
   * - Variant modifier: `hx-badge--{variant}` (omitted for 'fill')
   * - Context modifier: `hx-badge--{context}` (omitted for 'brand')
   * - Type modifier: `hx-badge--{type}` (omitted for 'numeric')
   */
  protected readonly badgeClasses = computed(() => {
    const variant = this.variant();
    const context = this.context();
    const type = this.type();
    const shape = this.shape();
    const leadingDot = this.showStatusDot();

    return {
      'hx-badge': true,
      // Variant modifier (omit 'fill' as it's default)
      [`hx-badge--${variant}`]: variant !== 'fill',
      // Context modifier (omit 'brand' as it's default)
      [`hx-badge--${context}`]: context !== 'brand',
      // Type modifier (omit 'numeric' as it's default, include 'dot' and 'text')
      [`hx-badge--${type}`]: type === 'dot' || type === 'text',
      // Shape modifier (omit rounded as it's default)
      [`hx-badge--${shape}`]: shape !== 'rounded',
      // Leading dot modifier
      'hx-badge--with-dot': leadingDot,
    };
  });

  /**
   * Formatted label text for template rendering.
   *
   * Handles display logic:
   *
   * - Returns empty string when `type` is 'dot'
   * - Returns "999+" when numeric value > 999
   * - Returns empty string for undefined/null
   * - Converts all other values to string
   */
  protected readonly displayLabel = computed(() => {
    // Dot badges have no label
    if (this.type() === 'dot') {
      return '';
    }

    const val = this.label();

    // Handle undefined/null
    if (val === undefined || val === null) {
      return '';
    }

    // Handle numeric values with 999+ cap
    if (typeof val === 'number') {
      return val > 999 ? '999+' : String(val);
    }

    // Handle string values
    return String(val);
  });

  protected readonly customBackgroundColor = computed(() => {
    if (this.backgroundColor()) {
      return this.backgroundColor();
    }

    if (this.type() !== 'dot' || !this.color()) {
      return null;
    }

    return this.color();
  });

  protected readonly customTextColor = computed(() => {
    if (this.textColor()) {
      return this.textColor();
    }

    return null;
  });

  protected readonly customBoxShadow = computed(() => {
    if (this.backgroundColor() || (this.type() === 'dot' && this.color())) {
      return 'none';
    }

    return null;
  });
}
